/**
 * ABOUTME: Chat card component for displaying top-performing content results.
 * Rendered when the analytics agent returns get_top_content results via message.metadata.top_content.
 */
import { Trophy } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CHAT_CARD } from '@/components/chat/chatCard'
import { formatDateRange } from '@/components/chat/chatCardDate'

interface ContentItem {
  id: string
  title: string
  platform: string
  published_at: string | null
  metrics: {
    engagement: number
    impressions: number
    clicks: number
    conversions: number
    revenue: number
  }
  rank: number
}

interface TopContentData {
  metric_type: string
  start_date: string
  end_date: string
  content: ContentItem[]
  total_content_analyzed: number
}

interface TopContentCardProps {
  data: TopContentData
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value)
}

function formatMetricLabel(metricType: string): string {
  const labels: Record<string, string> = {
    engagement: 'Engagement',
    reach: 'Reach',
    impressions: 'Impressions',
    clicks: 'Clicks',
    revenue: 'Revenue',
    conversions: 'Conversions',
  }
  return labels[metricType] || metricType.charAt(0).toUpperCase() + metricType.slice(1)
}

function getPrimaryMetric(item: ContentItem, metricType: string): string {
  const val = item.metrics[metricType as keyof typeof item.metrics] ?? 0
  if (metricType === 'revenue') return formatCurrency(val)
  return formatNumber(val)
}

function RankBadge({ rank }: { rank: number }) {
  const colors = [
    'bg-warning/20 text-warning',
    'bg-border/30 text-muted-foreground',
    'bg-warning/20 text-warning',
  ]
  const colorClass = rank <= 3 ? colors[rank - 1] : 'bg-muted text-muted-foreground'
  return (
    <span
      className={`inline-flex size-5 items-center justify-center rounded-full text-[0.625rem] font-bold ${colorClass}`}
    >
      {rank}
    </span>
  )
}

export function TopContentCard({ data }: TopContentCardProps) {
  if (data.content.length === 0) {
    return (
      <div
        className={cn(CHAT_CARD, 'text-muted-foreground my-2 text-center text-xs')}
        data-testid="top-content-card"
      >
        No content data available for this period.
      </div>
    )
  }

  return (
    <div className={cn(CHAT_CARD, 'my-2 space-y-2')} data-testid="top-content-card">
      <div>
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-1.5 text-sm font-medium">
            <Trophy className="text-muted-foreground size-4" />
            Top Content by {formatMetricLabel(data.metric_type)}
          </h3>
          <span className="text-muted-foreground text-xs">
            {formatDateRange(data.start_date, data.end_date)}
          </span>
        </div>
        <p className="text-muted-foreground text-xs">
          {data.total_content_analyzed} pieces analyzed
        </p>
      </div>
      <div>
        <div className="divide-border/50 divide-y">
          {data.content.slice(0, 5).map((item) => (
            <div key={item.id} className="flex items-center gap-2 py-1.5">
              <RankBadge rank={item.rank} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-medium">{item.title}</div>
                <div className="text-muted-foreground flex gap-2 text-xs">
                  <span>{item.platform}</span>
                  {item.metrics.revenue > 0 && (
                    <span>{formatCurrency(item.metrics.revenue)} rev</span>
                  )}
                  {item.metrics.clicks > 0 && (
                    <span>{formatNumber(item.metrics.clicks)} clicks</span>
                  )}
                  {item.metrics.conversions > 0 && (
                    <span>{formatNumber(item.metrics.conversions)} conv</span>
                  )}
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-sm font-bold">{getPrimaryMetric(item, data.metric_type)}</div>
              </div>
            </div>
          ))}
        </div>
        {data.content.length > 5 && (
          <p className="text-muted-foreground mt-2 text-center text-xs">
            +{data.content.length - 5} more items
          </p>
        )}
      </div>
    </div>
  )
}
